// src/pages/ForgotPassword.jsx
//
// Сброс забытого пароля в три шага:
//   1) email      → отправляем 6-значный код на почту
//   2) code       → проверяем код
//   3) password   → задаём новый пароль
//
// Роль (shop|customer) приходит пропом из Auth — та же, на чьём экране входа нажали «Забыли пароль».
// Язык интерфейса уходит на бэк, чтобы письмо пришло на языке пользователя.

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../api';
import { errorMessage } from '../utils/errorMessage';
import Icon from '../components/Icon';
import PasswordInput from '../components/PasswordInput';

const RESEND_COOLDOWN_SEC = 60;   // столько ждать до повторной отправки (совпадает с лимитом бэка)

export default function ForgotPassword({ role, initialEmail = '', onBack, onDone }) {
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState('email');       // email | code | password
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const timerRef = useRef(null);

  // Обратный отсчёт до повторной отправки
  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  // ─── Шаг 1: запросить код ───────────────────────────────────
  const requestCode = async () => {
    setError('');
    const mail = email.trim().toLowerCase();
    if (!mail) return setError(t('auth.errorEmailRequired'));

    setLoading(true);
    try {
      await auth.forgotPassword(mail, role, i18n.language);
      setStep('code');
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (e) {
      setError(errorMessage(e, t));
    }
    setLoading(false);
  };

  // ─── Шаг 2: проверить код ───────────────────────────────────
  const verifyCode = async () => {
    setError('');
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) return setError(t('forgot.errCodeFormat'));

    setLoading(true);
    try {
      await auth.verifyResetCode(email.trim().toLowerCase(), role, c);
      setStep('password');
    } catch (e) {
      // бэк может вернуть attemptsLeft — покажем, сколько попыток осталось
      const left = e?.data?.attemptsLeft;
      if (left !== undefined && left > 0) {
        setError(`${errorMessage(e, t)} · ${t('forgot.attemptsLeft', { count: left })}`);
      } else {
        setError(errorMessage(e, t));
      }
    }
    setLoading(false);
  };

  // ─── Шаг 3: задать новый пароль ─────────────────────────────
  const savePassword = async () => {
    setError('');
    if (password.length < 6) return setError(t('auth.errorPasswordShort'));
    if (password !== password2) return setError(t('forgot.errMismatch'));

    setLoading(true);
    try {
      await auth.resetPassword(email.trim().toLowerCase(), role, code.trim(), password);
      onDone?.();   // возвращаемся на вход
    } catch (e) {
      setError(errorMessage(e, t));
      // если код протух/сгорел — вернём на шаг ввода кода
      const msg = String(e?.message || '');
      if (/код/i.test(msg) || /code/i.test(msg)) setStep('code');
    }
    setLoading(false);
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      await auth.forgotPassword(email.trim().toLowerCase(), role, i18n.language);
      setCooldown(RESEND_COOLDOWN_SEC);
      setCode('');
    } catch (e) {
      setError(errorMessage(e, t));
    }
    setLoading(false);
  };

  const goBack = () => {
    if (step === 'code') { setStep('email'); setError(''); setCode(''); return; }
    if (step === 'password') { setStep('code'); setError(''); return; }
    onBack?.();
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      padding: 24,
      background: 'var(--bg)',
    }}>
      {/* назад */}
      <button
        onClick={goBack}
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top) + 12px)',
          left: 12,
          background: 'none',
          border: 'none',
          padding: 10,
          cursor: 'pointer',
          color: 'var(--fg-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 14,
          fontFamily: 'var(--font-display)',
        }}
      >
        <Icon name="chevronLeft" size={20} stroke={2.5} />
        {t('common.back')}
      </button>

      {/* шапка */}
      <div style={{ marginTop: 'calc(env(safe-area-inset-top) + 56px)', textAlign: 'center', marginBottom: 24 }}>
        <div style={{
          width: 64, height: 64, margin: '0 auto 14px',
          background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-accent))',
          borderRadius: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(99, 102, 241, 0.25)',
          color: '#fff',
        }}>
          <Icon name="lock" size={30} stroke={2} />
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 8 }}>
          {t('forgot.title')}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
          {step === 'email'    && t('forgot.hintEmail')}
          {step === 'code'     && t('forgot.hintCode', { email: email.trim().toLowerCase() })}
          {step === 'password' && t('forgot.hintPassword')}
        </p>
      </div>

      {/* ── Шаг 1: email ── */}
      {step === 'email' && (
        <>
          <div className="input-group">
            <label>{t('auth.email')}</label>
            <input
              className="input"
              type="email"
              placeholder="you@example.com"
              autoCapitalize="none"
              autoCorrect="off"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && requestCode()}
            />
          </div>
          {error && <ErrorText>{error}</ErrorText>}
          <button className="btn btn-primary btn-lg" onClick={requestCode} disabled={loading}>
            {loading ? t('common.loading') : t('forgot.sendCode')}
          </button>
        </>
      )}

      {/* ── Шаг 2: код ── */}
      {step === 'code' && (
        <>
          <div className="input-group">
            <label>{t('forgot.codeLabel')}</label>
            <input
              className="input"
              type="tel"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={e => e.key === 'Enter' && verifyCode()}
              style={{
                fontSize: 26,
                letterSpacing: 8,
                textAlign: 'center',
                fontWeight: 700,
                fontFamily: 'var(--font-display)',
              }}
            />
          </div>
          {error && <ErrorText>{error}</ErrorText>}
          <button className="btn btn-primary btn-lg" onClick={verifyCode} disabled={loading || code.length !== 6}>
            {loading ? t('common.loading') : t('common.confirm')}
          </button>
          <button
            onClick={resend}
            disabled={cooldown > 0 || loading}
            style={{
              marginTop: 14,
              background: 'none',
              border: 'none',
              color: cooldown > 0 ? 'var(--fg-muted)' : 'var(--brand-primary)',
              fontSize: 14,
              fontWeight: 600,
              cursor: cooldown > 0 ? 'default' : 'pointer',
              fontFamily: 'var(--font-display)',
            }}
          >
            {cooldown > 0
              ? t('forgot.resendIn', { sec: cooldown })
              : t('forgot.resend')}
          </button>
        </>
      )}

      {/* ── Шаг 3: новый пароль ── */}
      {step === 'password' && (
        <>
          <div className="input-group">
            <label>{t('forgot.newPassword')}</label>
            <PasswordInput
              className="input"
              placeholder="••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </div>
          <div className="input-group">
            <label>{t('forgot.repeatPassword')}</label>
            <PasswordInput
              className="input"
              placeholder="••••••"
              value={password2}
              onChange={e => setPassword2(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && savePassword()}
            />
          </div>
          {error && <ErrorText>{error}</ErrorText>}
          <button className="btn btn-primary btn-lg" onClick={savePassword} disabled={loading}>
            {loading ? t('common.saving') : t('forgot.savePassword')}
          </button>
        </>
      )}
    </div>
  );
}

function ErrorText({ children }) {
  return (
    <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12, textAlign: 'center', lineHeight: 1.4 }}>
      {children}
    </p>
  );
}
